/**
 * Mesa aberta fora da tela (docs/spec/open-rooms.md): uma conexão própria que só mantém a pessoa
 * dentro dela e conta o que chega no chat. Música, voz, fichas e cenas ficam com a mesa da tela.
 *
 * O servidor aceita várias conexões da mesma pessoa, cada uma numa mesa. Entrar por uma conexão
 * nova numa mesa em que ela já está não pede senha nem anuncia a entrada, e sair por uma delas não
 * anuncia a saída enquanto outra continuar lá; é o que deixa a troca de mesa passar despercebida.
 */
import { RpgDeskSocket } from '../socket.js';
import type { ChatMessage, GameSession, Room, RoomMember, RollResult, ServerMessage } from '../shared.js';

export type BackgroundEvents = {
  /** entrou (ou reentrou depois de cair) */
  onReady: (roomId: string, room: Room) => void;
  /** chegou fala ou rolagem de outra pessoa; `members` serve para achar o meu apelido */
  onActivity: (roomId: string, item: { message?: ChatMessage; roll?: RollResult }, context: { room: Room; members: Record<string, RoomMember> }) => void;
  onRenamed: (roomId: string, room: Room) => void;
  /** o relógio da sessão da mesa (`live`: mudou agora; senão, veio na entrada) */
  onClock: (roomId: string, session: GameSession | null, live: boolean) => void;
  /** expulso, banido ou mesa apagada */
  onRemoved: (roomId: string, reason: 'kicked' | 'banned' | 'deleted', byName: string) => void;
  /** a entrada foi recusada (senha, mesa sumiu) */
  onFailed: (roomId: string, message: string) => void;
};

export class BackgroundRoom {
  /** resolve true ao entrar, false se a entrada falhar ou a conexão for fechada antes */
  readonly ready: Promise<boolean>;
  private settle!: (ok: boolean) => void;
  private room: Room | null = null;
  private members: Record<string, RoomMember> = {};
  private readonly socket: RpgDeskSocket;

  constructor(
    readonly roomId: string,
    token: string,
    private readonly myId: string,
    private readonly events: BackgroundEvents,
    password: string | null = null,
  ) {
    this.ready = new Promise((resolve) => (this.settle = resolve));
    this.socket = new RpgDeskSocket({
      onMessage: (message) => this.apply(message, password),
      onStatus: () => undefined,
      onProblem: () => undefined,
    });
    this.socket.connect(token);
  }

  close(): void {
    this.socket.close();
    this.settle(false);
  }

  private apply(message: ServerMessage, password: string | null): void {
    switch (message.type) {
      case 'hello':
        // a cada conexão (inclusive depois de cair) entra de novo
        this.socket.send({ type: 'room.join', roomId: this.roomId, password });
        return;
      case 'ready':
        this.room = message.state.room;
        this.members = Object.fromEntries(message.state.members.map((member) => [member.userId, member]));
        this.events.onReady(this.roomId, this.room);
        this.events.onClock(this.roomId, message.state.gameSession, false);
        this.settle(true);
        return;
      case 'presence.join':
        if (message.member) this.members = { ...this.members, [message.user.id]: message.member };
        return;
      case 'presence.updated':
        this.members = { ...this.members, [message.member.userId]: message.member };
        return;
      case 'room.updated':
        this.room = message.room;
        this.events.onRenamed(this.roomId, message.room);
        return;
      case 'gameSession.state':
        // o anel no botão da mesa continua andando com a mesa fora da tela
        this.events.onClock(this.roomId, message.session, true);
        return;
      case 'chat.message':
        if (this.room && message.message.userId !== this.myId && message.message.kind !== 'system') {
          this.events.onActivity(this.roomId, { message: message.message }, { room: this.room, members: this.members });
        }
        return;
      case 'dice.result':
        // a rolagem também chega como o resumo no chat (`kind: 'roll'`, com o canal, se privada),
        // que já conta: contar aqui também daria duas não lidas por rolagem
        return;
      case 'room.removed':
        this.close();
        this.events.onRemoved(this.roomId, message.reason, message.byName);
        return;
      case 'error':
        // antes de entrar, erro é recusa da entrada; depois, as ações desta conexão são só a entrada
        if (!this.room) {
          this.close();
          this.events.onFailed(this.roomId, message.message);
        }
        return;
      default:
        // o resto (contatos, mensagens diretas, fichas, áudio, voz) é da conexão da mesa da tela
        return;
    }
  }
}
